import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Zap } from "lucide-react";
import { api, shortAddr } from "@/lib/api";
import { fmtSui } from "@/lib/format";
import { useLive } from "@/context/LiveContext";
import TrendArrow from "@/components/TrendArrow";

export default function TradeTicker() {
  const [trades, setTrades] = useState([]);
  const [flash, setFlash] = useState(new Set());
  const prev = useRef(new Set());
  const navigate = useNavigate();
  const { subscribe } = useLive();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/trades/recent", { params: { limit: 20 } });
        if (!alive) return;
        const keyOf = (t) => `${t.digest}-${t.ts}`;
        prev.current = new Set(data.map(keyOf));
        setTrades(data);
      } catch (e) {}
    };
    load();
    const t = setInterval(load, 30000); // slow safety refresh; live WS drives updates
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Real-time: prepend each new on-chain trade as it happens and flash it.
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "trade") return;
      const key = `${msg.digest}-${msg.ts}`;
      if (prev.current.has(key)) return;
      prev.current.add(key);
      // Bound the dedup set so a long mobile session doesn't grow it unboundedly (a slow leak
      // that only a full Slush reload clears). Keep it near the visible window size.
      if (prev.current.size > 60) prev.current = new Set(Array.from(prev.current).slice(-40));
      setTrades((cur) => [msg, ...cur].slice(0, 20));
      setFlash(new Set([key]));
      setTimeout(() => setFlash(new Set()), 1600);
    });
  }, [subscribe]);

  if (!trades.length) return null;

  return (
    <div className="border-b border-white/10 bg-black/40" data-testid="trade-ticker">
      <div className="max-w-[1400px] mx-auto flex items-center gap-2 px-3 sm:px-4 py-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        <span className="shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1EC62E] pr-1">
          <Zap className="w-3.5 h-3.5" /> Live
        </span>
        {trades.map((t, i) => {
          const key = `${t.digest}-${t.ts}`;
          const isNew = flash.has(key);
          const buy = t.is_buy;
          const col = buy ? "#22C55E" : "#F43F5E";
          return (
            <button
              key={key + i}
              onClick={() => navigate(`/coin/${t.curve_id}`)}
              data-testid={`ticker-trade-${i}`}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-mono transition-all duration-500"
              style={{
                borderColor: isNew ? col : "rgba(255,255,255,0.08)",
                background: isNew ? `${col}1a` : "rgba(255,255,255,0.03)",
                boxShadow: isNew ? `0 0 14px ${col}55` : "none",
              }}
            >
              {buy ? <TrendArrow up size={14} /> : <TrendArrow up={false} size={14} />}
              <span className="font-bold" style={{ color: col }}>{buy ? "BUY" : "SELL"}</span>
              <span className="text-white">${t.symbol}</span>
              <span className="text-muted-foreground">{fmtSui(t.sui_amount, 3)} SUI</span>
              {t.is_dev ? (
                <span className="px-1 rounded bg-[#FFB800]/20 text-[#FFB800] text-[9px] font-bold">DEV</span>
              ) : (
                <span className="text-muted-foreground/70">{shortAddr(t.trader)}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
